import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  useTheme,
  Grid,
  FormControl,
  InputBase,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CardActions,
  Avatar,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ClearOutlined,
  Edit,
  ModeEditOutlineOutlined,
  Search,
  CancelOutlined,
  AddPhotoAlternate,
  Close,
  RestoreOutlined,
} from "@mui/icons-material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import {
  GetPackageList,
  DeletePackage,
  CancelPackage,
  ReactivatePackage,
  uploadPackagePhoto,
  deletePackagePhoto,
} from "api/packages";
import { SubmitPackageCancelRequest } from "api/packageChangeRequest";
import { getFileExtension } from "utils/fileUtils";
import { getPhotoUrl } from "utils";
import { hasPageAccess } from "utils/pagePermissions";
import { useUserRefreshTick } from "hooks/useUserRefreshTick";
import { GetCustomersList } from "api/customers";
import { GetStationList } from "api/stations";
import Swal from "sweetalert2";
import { playAlertSound, playCorrectSound } from "utils";
import { isMobile } from "react-device-detect";
import FlexBetween from "components/FlexBetween";
import moment from "moment";
import { user } from "localStorage";
import PackageEditDialog from "components/PackageEditDialog";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import { Link } from "react-router-dom";
import StyledDataGrid from "components/bento/StyledDataGrid";
import BentoCard from "components/bento/BentoCard";
import { BG_TINT, BG_WHITE, BORDER, RADIUS_INPUT, RADIUS_TILE, CARD_SHADOW, PURPLE_LIGHT, PURPLE_DARK, TEXT_SECONDARY, TEXT_SUBTLE } from "components/bento/bentoTokens";

// Mirrors Package.cancelReason's enum (backend/models/Package.js) — see
// backend/services/packageCancelService.js for the cancellation rules.
const CANCEL_REASON_OPTIONS = ["customer_request", "no_pickup", "duplicate", "undeliverable", "other"];
const CANCEL_REASON_LABEL_KEYS = {
  customer_request: "packages.cancel.reasonCustomerRequest",
  no_pickup: "packages.cancel.reasonNoPickup",
  duplicate: "packages.cancel.reasonDuplicate",
  undeliverable: "packages.cancel.reasonUndeliverable",
  other: "packages.cancel.reasonOther",
};
const NOT_CANCELLABLE_STATUSES = ["Delivered", "Cancelled"];
const MAX_CANCEL_PHOTOS = 6;

const PackageList = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Pagination, sorting, search
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");

  // Package data
  const [packageData, setPackageData] = useState(null);
  // State for the edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // State for the cancel dialog — see CancelPackage (api/packages.js) and
  // backend/services/packageCancelService.js.
  const [cancelDialogPkg, setCancelDialogPkg] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNote, setCancelNote] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelPhotos, setCancelPhotos] = useState([]);
  const [uploadingCancelPhoto, setUploadingCancelPhoto] = useState(false);
  const cancelPhotoInputRef = useRef(null);

  // DataGrid rows and total count
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filters: customer, station, status, updated-at date range
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [stationList, setStationList] = useState([]);
  const [selectedStationId, setSelectedStationId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const statusOptions = Array.from(
    new Set(
      ["Pending", ...stationList.map((s) => s.parcelStatus).filter(Boolean)]
    )
  );

  const clearFilters = () => {
    setSelectedCustomer(null);
    setCustomerSearchInput("");
    setSelectedStationId("");
    setSelectedStatus("");
    setFromDate(null);
    setToDate(null);
  };

  // User and role check
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js
  const _user = user();
  const isAgentUser = _user.role === "agent";
  const canCancelPackage = hasPageAccess(_user, "packages/cancel");
  // Roles without direct cancel access (e.g. "manager") can still request a
  // cancel — it just goes through /api/package-change-request and waits for
  // admin approval instead of applying immediately. See
  // backend/scripts/seed-package-change-request-permissions.js.
  const canRequestPackageChange = hasPageAccess(_user, "packages/change-requests");
  // Same reasoning for the Edit column: admin edits directly, a role granted
  // packages/change-requests (e.g. "manager") can open the dialog to submit an
  // edit request, and everyone else (e.g. the base "user" role, which the seed
  // script deliberately does NOT grant this to) never sees the button at all —
  // PackageEditDialog's own canEditDirectly check mirrors this same condition.
  const canEditPackage = _user.role === "admin" || canRequestPackageChange;

  // Fetch package list from API
  const fetchPackages = (page, pageSize, sort, search) => {
    setIsLoading(true);
    const filters = {
      customerId: selectedCustomer ? selectedCustomer._id : undefined,
      station: selectedStationId || undefined,
      status: selectedStatus || undefined,
      fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined,
      toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : undefined,
    };
    GetPackageList(page, pageSize, sort, search, filters)
      .then((res) => {
        if (res.status === 200) {
          if (isMobile) {
            setRows((prevRows) => {
              const newRows = res.data.filter(
                (newItem) =>
                  !prevRows.some(
                    (existingItem) => existingItem._id === newItem._id
                  )
              );
              return [...prevRows, ...newRows];
            });
          } else {
            setRows(res.data);
          }
          setTotalCount(res.total);
        } else {
          console.error(res.message);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Edit handler: pre-fill state and open edit dialog
  const buttonEditHandler = (rowValue) => {
    setPackageData(rowValue);
    setEditDialogOpen(true);
  };

  // Handle dialog close and clear package data
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    // Clear packageData to ensure fresh state for next edit
    setPackageData(null);
  };

  // Delete handler
  const buttonDeleteHandler = (event, cellValues) => {
    Swal.fire({
      title: `Delete ${cellValues.row.tracking}?`,
      text: "The status of this package will be changed to deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        DeletePackage(cellValues.row._id).then((res) => {
          if (res.status === 200) {
            setRows((prevRows) =>
              prevRows.filter((item) => item._id !== cellValues.id)
            );
            playCorrectSound();
            Swal.fire({
              title: "Deleted!",
              text: res.message,
              icon: "success",
              timer: 1500,
            });
          } else {
            playAlertSound();
            Swal.fire({
              backdrop: true,
              toast: false,
              icon: "error",
              text: res.message || "Failed to delete package",
              showConfirmButton: true,
            });
          }
        });
      }
    });
  };

  // Cancel handlers
  const openCancelDialog = (pkg) => {
    setCancelDialogPkg(pkg);
    setCancelReason("");
    setCancelNote("");
    setCancelPhotos([]);
  };

  const closeCancelDialog = () => {
    setCancelDialogPkg(null);
    setCancelReason("");
    setCancelNote("");
    setCancelPhotos([]);
  };

  // Evidence photos are uploaded immediately (same UX as Parcel Monitoring's
  // evidence uploader) — they attach to the package as soon as picked, tagged
  // category "cancel_evidence", independent of whether Cancel is confirmed yet.
  const handleCancelPhotoSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0 || !cancelDialogPkg) return;
    const remaining = MAX_CANCEL_PHOTOS - cancelPhotos.length;
    const toUpload = files.slice(0, Math.max(remaining, 0));
    setUploadingCancelPhoto(true);
    for (const file of toUpload) {
      try {
        const ext = getFileExtension(file);
        const res = await uploadPackagePhoto(
          cancelDialogPkg.tracking,
          file,
          ext,
          { lat: "", lng: "" },
          "cancel_evidence"
        );
        if (res?.success && res.data?.photos) {
          setCancelPhotos(res.data.photos.filter((p) => p.category === "cancel_evidence"));
        }
      } catch (err) {
        Swal.fire({ icon: "error", title: t("packages.cancel.errorTitle"), text: err.message });
      }
    }
    setUploadingCancelPhoto(false);
  };

  const handleRemoveCancelPhoto = async (photoPath) => {
    if (!cancelDialogPkg) return;
    try {
      await deletePackagePhoto(cancelDialogPkg.tracking, photoPath);
      setCancelPhotos((prev) => prev.filter((p) => p.path !== photoPath));
    } catch (err) {
      // leave the photo in place if the delete failed
    }
  };

  const confirmCancel = async () => {
    if (!cancelReason || !cancelDialogPkg) return;
    setCancelling(true);

    if (!canCancelPackage) {
      // No direct cancel permission — submit for admin approval instead.
      const res = await SubmitPackageCancelRequest(cancelDialogPkg._id, cancelReason, cancelNote);
      setCancelling(false);
      if (res.success) {
        playCorrectSound();
        Swal.fire({ title: t("packageChangeRequest.submittedTitle"), icon: "success", text: t("packageChangeRequest.submittedText"), timer: 2000 });
        closeCancelDialog();
      } else {
        playAlertSound();
        Swal.fire({ icon: "error", title: t("packageChangeRequest.errorTitle"), text: res.message });
      }
      return;
    }

    const res = await CancelPackage(cancelDialogPkg._id, cancelReason, cancelNote);
    setCancelling(false);
    if (res.status === 200) {
      setRows((prevRows) =>
        prevRows.map((item) => (item._id === cancelDialogPkg._id ? { ...item, ...res.data } : item))
      );
      playCorrectSound();
      Swal.fire({ title: t("packages.cancel.successTitle"), icon: "success", timer: 1500 });
      closeCancelDialog();
    } else {
      playAlertSound();
      Swal.fire({ icon: "error", title: t("packages.cancel.errorTitle"), text: res.message });
    }
  };

  // Reactivate handler — undoes a cancellation (restores the pre-cancel status).
  const buttonReactivateHandler = (event, cellValues) => {
    Swal.fire({
      title: t("packages.cancel.reactivateTitle"),
      text: cellValues.row.tracking,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("packages.cancel.reactivateConfirm"),
    }).then((result) => {
      if (!result.isConfirmed) return;
      ReactivatePackage(cellValues.row._id).then((res) => {
        if (res.status === 200) {
          setRows((prevRows) =>
            prevRows.map((item) => (item._id === cellValues.row._id ? { ...item, ...res.data } : item))
          );
          playCorrectSound();
          Swal.fire({ title: t("packages.cancel.reactivateSuccessTitle"), icon: "success", timer: 1500 });
        } else {
          playAlertSound();
          Swal.fire({ icon: "error", title: t("packages.cancel.errorTitle"), text: res.message });
        }
      });
    });
  };

  // Define DataGrid columns (omitting details here for brevity, assume columns are defined as before)
  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      hide: true,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 0.7,
      valueGetter: (params) => params.row.customer && params.row.customer.name,
    },
    {
      field: "tracking",
      headerName: "Tracking",
      flex: 1,
      renderCell: (cellValues) => (
        <Link
          component="button"
          color="secondary"
          underline="hover"
          onClick={() => {
            setTrackingNumber(cellValues.row.tracking);
            setOpen(true);
          }}
        >
          {cellValues.row.tracking}
        </Link>
      ),
    },
    {
      field: "orderId",
      headerName: "Order ID",
      flex: 0.5,
      hide: true,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
    },
    {
      field: "routeId",
      headerName: "Route ID",
      flex: 0.5,
      hide: isMobile,
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.3,
      hide: true,
    },
    // {
    //   field: "quantity",
    //   headerName: "QTY",
    //   flex: 0.5,
    // },

    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      hide: false,
    },
    {
      field: "station",
      headerName: "Station",
      flex: 0.3,
      hide: isMobile,
    },
    {
      field: "isCOD",
      headerName: "COD",
      flex: 0.3,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isCOD ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "shippingFee",
      headerName: "ຄ່າບໍລິການ",
      flex: 0.3,
    },
    {
      field: "isForwardRequired",
      headerName: "ສົ່ງຕໍ່",
      flex: 0.3,
      hide: true,
      renderCell: (cellValues) => {
        return cellValues.row.isForwardRequired ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isDeliveryRequired",
      headerName: "ສົ່ງຢູ່ບ້ານ",
      flex: 0.3,
      hide: true,
      renderCell: (cellValues) => {
        return cellValues.row.isDeliveryRequired ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isSelfOrder",
      headerName: "ສັ່ງເອງ",
      flex: 0.3,
      hide: true,
      renderCell: (cellValues) => {
        return cellValues.row.isSelfOrder ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isSubCustomer",
      headerName: "ລູກຄ້າຕົວແທນ",
      flex: 0.3,
      hide: false,
      renderCell: (cellValues) => {
        return cellValues.row.isSubCustomer ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "agentId",
      headerName: "ຕົວແທນ",
      flex: 0.3,
      hide: false,
    },
    {
      field: "finalShippingFee",
      headerName: "Final Fee",
      flex: 0.3,
      hide: false,
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 0.7,
      hide: isMobile,
      valueGetter: (params) =>
        moment(params.row.updatedAt)
          .tz("Asia/Vientiane")
          .format("DD/MM/YYYY, h:mm:ss A"),
    },
    {
      field: "updatedBy",
      headerName: "Updated by",
      flex: 0.3,
      hide: true,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.7,
      hide: true,
      valueGetter: (params) =>
        moment(params.row.createdAt)
          .tz("Asia/Vientiane")
          .format("DD/MM/YYYY, h:mm:ss A"),
    },
    {
      field: "createdBy",
      headerName: "Created by",
      flex: 0.3,
      hide: true,
    },

    // Edit is hidden entirely for a role with neither direct edit access (admin)
    // nor packages/change-requests (submit-a-request access) — previously this
    // button was unconditional, so even a plain "user" role with no grant at all
    // saw it and could open the dialog (submission then just 403'd, or silently
    // succeeded if that role had been granted the permission some other way).
    ...(canEditPackage
      ? [
          {
            field: "edit",
            headerName: "Edit",
            flex: 0.2,
            renderCell: (cellValues) => {
              return (
                <IconButton
                  variant="outline"
                  onClick={() => {
                    buttonEditHandler(cellValues.row);
                  }}
                  sx={{ "&:hover": { color: theme.palette.warning.main } }}
                >
                  <ModeEditOutlineOutlined />
                </IconButton>
              );
            },
          },
        ]
      : []),
    {
      field: "del",
      headerName: "Delete",
      flex: 0.2,
      hide: true,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outline"
            sx={{ "&:hover": { color: theme.palette.error.main } }}
            onClick={(event) => buttonDeleteHandler(event, cellValues)}
          >
            <ClearOutlined />
          </IconButton>
        );
      },
    },
    // Cancel/reactivate is its own permission (packages/cancel) — not everyone
    // who can edit packages can also cancel them. See
    // backend/scripts/seed-package-cancel-permissions.js. A role with only
    // packages/change-requests (e.g. "manager") still gets the Cancel button,
    // but confirming it submits a request for admin approval instead of
    // cancelling immediately — see confirmCancel below.
    ...(canCancelPackage || canRequestPackageChange
      ? [
          {
            field: "cancel",
            headerName: t("packages.cancel.button"),
            flex: 0.2,
            renderCell: (cellValues) => {
              if (cellValues.row.status === "Cancelled") {
                // Reactivating a cancellation is direct-permission only —
                // not part of the manager-approval request flow.
                if (!canCancelPackage) return null;
                return (
                  <IconButton
                    variant="outline"
                    sx={{ "&:hover": { color: theme.palette.success.main } }}
                    onClick={(event) => buttonReactivateHandler(event, cellValues)}
                  >
                    <RestoreOutlined />
                  </IconButton>
                );
              }
              const blocked = NOT_CANCELLABLE_STATUSES.includes(cellValues.row.status);
              return (
                <IconButton
                  variant="outline"
                  disabled={blocked}
                  sx={{ "&:hover": { color: theme.palette.error.main } }}
                  onClick={() => openCancelDialog(cellValues.row)}
                >
                  <CancelOutlined />
                </IconButton>
              );
            },
          },
        ]
      : []),
  ];

  // Debounce search
  let timer;
  const setSearchWithDelaying = (val) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => setSearch(val), 1000);
  };

  // Infinite scroll for mobile — listens on the layout's main scroll container
  useEffect(() => {
    if (!isMobile) return;
    const container = document.getElementById("main-scroll-container") || window;

    const handleScroll = () => {
      const el = /** @type {Element} */ (container);
      const scrollTop    = container === window ? window.scrollY     : el.scrollTop;
      const clientHeight = container === window ? window.innerHeight : el.clientHeight;
      const scrollHeight = container === window ? document.documentElement.scrollHeight : el.scrollHeight;
      if (scrollTop + clientHeight >= scrollHeight - 200 && !isLoading && rows.length < totalCount) {
        setPage((prevPage) => prevPage + 1);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isLoading, rows.length, totalCount]);

  // Fetch station list once (also used to derive Status filter options)
  useEffect(() => {
    GetStationList().then((res) => {
      if (res && res.success && Array.isArray(res.data)) {
        setStationList(res.data);
      }
    });
  }, []);

  // Search customers for the Customer filter as the user types
  useEffect(() => {
    GetCustomersList(customerSearchInput).then((res) => {
      if (res.success && res.data) {
        setCustomerOptions(res.data);
      } else {
        setCustomerOptions([]);
      }
    });
  }, [customerSearchInput]);

  const filterDeps = [
    selectedCustomer ? selectedCustomer._id : "",
    selectedStationId,
    selectedStatus,
    fromDate,
    toDate,
  ];

  // Refresh package list when page, pageSize, sort, search, or filters change
  useEffect(() => {
    fetchPackages(page, pageSize, sort, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sort, search, ...filterDeps]);

  // Reset rows when search or filters change
  useEffect(() => {
    setRows([]);
    setPage(0);
    fetchPackages(0, pageSize, sort, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, ...filterDeps]);

  return (
    <Box>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <BentoCard sx={{ mb: "16px" }} bodySx={{ p: "20px 22px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(5, 1fr)" },
            gap: "16px",
            alignItems: "start",
          }}
        >
          <Autocomplete
            fullWidth
            options={customerOptions}
            value={selectedCustomer}
            onInputChange={(event, newInputValue) => setCustomerSearchInput(newInputValue)}
            onChange={(event, value) => setSelectedCustomer(value)}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) => {
              if (!option || !option.name) return "";
              return option.name + (option.whatsapp ? ` - ${option.whatsapp}` : "");
            }}
            renderInput={(params) => <TextField {...params} label="Customer" />}
          />
          <FormControl fullWidth>
            <InputLabel>Station</InputLabel>
            <Select
              label="Station"
              value={selectedStationId}
              onChange={(e) => setSelectedStationId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {stationList.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {id} ({name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              label="Updated From"
              inputFormat="DD/MM/yyyy"
              value={fromDate}
              maxDate={toDate || undefined}
              onChange={(newValue) => setFromDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              label="Updated To"
              inputFormat="DD/MM/yyyy"
              value={toDate}
              minDate={fromDate || undefined}
              onChange={(newValue) => setToDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ mt: "16px", display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" color="secondary" onClick={clearFilters}>
            Clear filters
          </Button>
        </Box>
      </BentoCard>
      {!isMobile ? (
        <Box height="80vh">
          <StyledDataGrid
            loading={isLoading}
            getRowId={(row) => row._id}
            rows={rows || []}
            columns={columns}
            rowCount={totalCount || 0}
            rowsPerPageOptions={[20, 50, 100]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode="server"
            sortingMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            onSortModelChange={(newSortModel) => setSort(...newSortModel)}
            components={{ Toolbar: DataGridCustomToolbar }}
            componentsProps={{
              toolbar: { searchInput, setSearchInput, setSearch },
            }}
            containerSx={{ height: "100%" }}
            autoHeight={false}
          />
        </Box>
      ) : (
        <Box>
          <Grid container spacing={1.5} width="auto">
            <Grid item xl={4} md={6} xs={12}>
              <FlexBetween
                backgroundColor={BG_TINT}
                border={`1px solid ${BORDER}`}
                borderRadius={RADIUS_INPUT}
                gap="3rem"
                p="0.7rem 1.5rem"
              >
                <InputBase
                  placeholder="Search..."
                  onChange={(e) => setSearchWithDelaying(e.target.value)}
                />
                <IconButton>
                  <Search />
                </IconButton>
              </FlexBetween>
            </Grid>
            {rows &&
              rows.map((packageItem, index) => (
                <Grid item key={index} xl={4} md={6} xs={12} width="auto">
                  <Card sx={{ background: BG_WHITE, border: `1px solid ${BORDER}`, borderRadius: RADIUS_TILE, boxShadow: CARD_SHADOW }}>
                    <CardHeader
                    onClick={() => {
                      setTrackingNumber(packageItem.tracking);
                      setOpen(true);
                    }}
                    sx={{ cursor: "pointer" }}
                      avatar={
                        <Avatar
                          sx={{
                            bgcolor:
                              parseInt(packageItem.shippingFee) !== 0
                                ? PURPLE_LIGHT
                                : TEXT_SUBTLE,
                          }}
                          aria-label="package"
                        >
                          {isAgentUser
                            ? parseInt(packageItem.amount) +
                            parseInt(packageItem.finalShippingFee)
                            : parseInt(packageItem.amount) +
                            parseInt(packageItem.shippingFee)}
                        </Avatar>
                      }
                      title={packageItem.tracking}
                      subheader={moment(packageItem.updatedAt)
                        .tz("Asia/Vientiane")
                        .format("DD/MM/YYYY, hh:mm:ss")}
                      action={
                        <FormControl sx={{ mr: 1, mt: 0.5 }}>
                          <Typography
                            variant="body1"
                            color={PURPLE_DARK}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            {packageItem.customer && packageItem.customer.name}
                            {packageItem.isSubCustomer &&
                              ` - (${packageItem.agentId})`}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={TEXT_SECONDARY}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            {packageItem.customer &&
                              packageItem.customer.whatsapp}
                          </Typography>
                        </FormControl>
                      }
                    />
                    <CardContent>
                      <FlexBetween sx={{ marginTop: -2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Order ID:{" "}
                          {packageItem.orderId
                            ? packageItem.orderId
                            : "NA / ບໍ່ມີ"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          at {packageItem.station} ({packageItem.routeId})
                        </Typography>
                      </FlexBetween>
                      <Typography variant="body2" color="text.secondary">
                        {packageItem.description}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={TEXT_SECONDARY}
                      >
                        {packageItem.remark
                          ? "Remark: " + packageItem.remark
                          : ""}
                      </Typography>
                    </CardContent>
                    <CardActions
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Box sx={{ flexGrow: 1, marginLeft: 1 }}>
                        <Typography color={TEXT_SUBTLE}>
                          #{index + 1}
                        </Typography>
                      </Box>
                      <IconButton
                        aria-label="Edit package"
                        onClick={() => buttonEditHandler(packageItem)}
                      >
                        <Edit />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>
      )}
      {/* Edit Package Dialog */}
      <PackageEditDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        packageData={packageData}
        rows={rows}
        setRows={setRows}
      />
      <TrackingDetailDialog
        open={open}
        onClose={() => setOpen(false)}
        trackingNumber={trackingNumber}
      />
      <Dialog open={!!cancelDialogPkg} onClose={closeCancelDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t("packages.cancel.dialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {cancelDialogPkg?.tracking}
          </Typography>
          {cancelDialogPkg?.paymentStatus && (
            <Typography variant="body2" sx={{ mb: 2, color: theme.palette.warning.main }}>
              {t("packages.cancel.refundWarning")}
            </Typography>
          )}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t("packages.cancel.reasonLabel")}</InputLabel>
            <Select
              label={t("packages.cancel.reasonLabel")}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            >
              {CANCEL_REASON_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>{t(CANCEL_REASON_LABEL_KEYS[r])}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label={t("packages.cancel.noteLabel")}
            value={cancelNote}
            onChange={(e) => setCancelNote(e.target.value)}
            sx={{ mb: 2 }}
          />

          {canCancelPackage && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("packages.cancel.photosLabel", { count: cancelPhotos.length, max: MAX_CANCEL_PHOTOS })}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {cancelPhotos.map((photo) => (
                  <Box
                    key={photo.path}
                    sx={{
                      position: "relative", width: 72, height: 72, borderRadius: "10px",
                      overflow: "hidden", border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box component="img" src={getPhotoUrl(photo)} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveCancelPhoto(photo.path)}
                      sx={{
                        position: "absolute", top: 2, right: 2, width: 20, height: 20,
                        background: "rgba(0,0,0,0.55)", color: "#fff",
                        "&:hover": { background: "rgba(0,0,0,0.75)" },
                      }}
                    >
                      <Close sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}
                {cancelPhotos.length < MAX_CANCEL_PHOTOS && (
                  <Box
                    onClick={() => !uploadingCancelPhoto && cancelPhotoInputRef.current?.click()}
                    sx={{
                      width: 72, height: 72, borderRadius: "10px", border: "1.5px dashed #c4c4c4",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: uploadingCancelPhoto ? "default" : "pointer",
                    }}
                  >
                    {uploadingCancelPhoto ? <CircularProgress size={18} color="secondary" /> : <AddPhotoAlternate color="disabled" />}
                  </Box>
                )}
              </Box>
              <input
                ref={cancelPhotoInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleCancelPhotoSelected}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" disabled={cancelling} onClick={closeCancelDialog}>
            {t("packages.cancel.back")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={cancelling || !cancelReason}
            onClick={confirmCancel}
          >
            {canCancelPackage ? t("packages.cancel.confirm") : t("packageChangeRequest.submitButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackageList;
